/**
 * Pure-function rule engine for Engage auto-launch decisions.
 *
 * Mirrors TheSkinnyAI's `autolaunch-evaluate` semantics bit-for-bit so the
 * three migrated TSAI tenants experience zero behavioral drift on cutover.
 * All functions are deterministic given their inputs (no `Date.now()` calls,
 * no DOM access, no storage reads) so they can be exercised exhaustively in
 * vitest without timers, jsdom, or storage mocks.
 *
 * Lexicon (kept consistent across this file + the public config schema):
 *   - `freq` / `frequency`: 'once_per_session' | 'once_per_day' | 'always'
 *     (legacy 'always' is mapped to 'once_per_session' at the read boundary
 *     per TSAI tribal knowledge; runtime defends against either spelling).
 *   - `cooldownSec`: minimum gap between fires (seconds). When `null` and
 *     `freq === 'once_per_day'`, we synthesize an 86400 floor.
 *   - `maxImpressionsPerSession`: hard cap on fires within one tab session;
 *     `0` is treated as "no cap" to match TSAI.
 *   - `pathInclude` / `pathExclude`: simple `startsWith` checks against the
 *     current URL pathname; `/` matches everything; empty include = match all.
 *   - `deviceInclude`: subset of {'desktop','tablet','mobile'}; empty = match all.
 *   - `localeInclude`: BCP-47 prefixes (e.g. ['en','fr']) matched against
 *     navigator.language case-insensitively; empty = match all.
 *   - Schedule window: `[startAt, endAt]` ISO timestamps; either may be null.
 */

export type AutoLaunchFrequency = 'once_per_session' | 'once_per_day' | 'always';

export type AutoLaunchDevice = 'desktop' | 'tablet' | 'mobile';

/** All reasons a decision can resolve to. Keep stable — used in vitest + telemetry. */
export type AutoLaunchSkipReason =
  | 'disabled'
  | 'stateless'
  | 'active_conversation'
  | 'resumed_history'
  | 'session_done'
  | 'manual_close_cooldown'
  | 'impression_cap'
  | 'cooldown_active'
  | 'schedule_before_start'
  | 'schedule_after_end'
  | 'path_excluded'
  | 'path_not_included'
  | 'device_not_included'
  | 'locale_not_included';

export type AutoLaunchDecision =
  | { readonly fire: true }
  | { readonly fire: false; readonly reason: AutoLaunchSkipReason };

const FIRE: AutoLaunchDecision = { fire: true };
const skip = (reason: AutoLaunchSkipReason): AutoLaunchDecision => ({ fire: false, reason });

// ---------------------------------------------------------------------------
// Frequency normalization (TSAI legacy mapping)
// ---------------------------------------------------------------------------

/**
 * Map raw frequency strings (including legacy `'always'`) to the canonical
 * three-value enum. Per `docs/legacy/tsai-tribal-knowledge.md`, TSAI's
 * `AutoLaunchModal.jsx` silently mapped `'always'` → `'once_per_session'`;
 * we honor that contract so migrated rows behave identically.
 */
export function mapLegacyFrequency(raw: string | null | undefined): AutoLaunchFrequency {
  const f = String(raw ?? '').toLowerCase().trim();
  if (f === 'once_per_day') return 'once_per_day';
  if (f === 'always') return 'once_per_session';
  return 'once_per_session';
}

// ---------------------------------------------------------------------------
// Path / device / locale / schedule predicates
// ---------------------------------------------------------------------------

function normalizeArray(input: readonly string[] | null | undefined): readonly string[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter((v) => v.length > 0);
}

/**
 * `true` if `path` matches at least one prefix in `include` and matches no
 * prefix in `exclude`. Empty `include` means "match all paths".
 */
export function matchesPathFilter(
  path: string,
  include: readonly string[] | null | undefined,
  exclude: readonly string[] | null | undefined,
): boolean {
  const p = typeof path === 'string' && path.length > 0 ? path : '/';
  const inc = normalizeArray(include);
  const exc = normalizeArray(exclude);
  if (exc.some((prefix) => p === prefix || p.startsWith(prefix))) return false;
  if (inc.length === 0) return true;
  return inc.some((prefix) => p === prefix || p.startsWith(prefix));
}

/**
 * `true` if `device` is in the include set. Empty set means "match all devices".
 * Unknown device strings (e.g. uppercased, whitespace) are normalized.
 */
export function matchesDeviceFilter(
  device: AutoLaunchDevice | string | null | undefined,
  include: readonly string[] | null | undefined,
): boolean {
  const inc = normalizeArray(include).map((v) => v.toLowerCase());
  if (inc.length === 0) return true;
  const d = String(device ?? '').toLowerCase().trim();
  if (!d) return false;
  return inc.includes(d);
}

/**
 * `true` if `language` (e.g. 'en-US') starts with any BCP-47 prefix in
 * `include` (case-insensitive). Empty set means "match all locales".
 */
export function matchesLocaleFilter(
  language: string | null | undefined,
  include: readonly string[] | null | undefined,
): boolean {
  const inc = normalizeArray(include).map((v) => v.toLowerCase());
  if (inc.length === 0) return true;
  const lang = String(language ?? '').toLowerCase().trim();
  if (!lang) return false;
  return inc.some((prefix) => lang === prefix || lang.startsWith(`${prefix}-`) || lang.startsWith(prefix));
}

/**
 * `null` if inside (or no bounds), or a skip reason if outside the window.
 * `startAt` / `endAt` are ISO-8601 strings; bad timestamps are ignored.
 */
export function evaluateScheduleWindow(
  nowMs: number,
  startAt: string | null | undefined,
  endAt: string | null | undefined,
): AutoLaunchSkipReason | null {
  if (startAt) {
    const t = Date.parse(startAt);
    if (Number.isFinite(t) && nowMs < t) return 'schedule_before_start';
  }
  if (endAt) {
    const t = Date.parse(endAt);
    if (Number.isFinite(t) && nowMs >= t) return 'schedule_after_end';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Cooldown + impression-cap evaluators
// ---------------------------------------------------------------------------

/**
 * `null` if no cooldown applies (or cooldown is satisfied), or
 * `'cooldown_active'` if a fire would violate the gap. `'always'` frequency
 * bypasses cooldown (matches TSAI semantics for that legacy flag, even though
 * we normalize at the read boundary).
 */
export function evaluateCooldown(params: {
  readonly frequency: AutoLaunchFrequency;
  readonly cooldownSec: number | null | undefined;
  readonly lastFireMs: number;
  readonly nowMs: number;
}): AutoLaunchSkipReason | null {
  const { frequency, cooldownSec, lastFireMs, nowMs } = params;
  if (frequency === 'always') return null;
  let cd = cooldownSec == null ? null : Number(cooldownSec);
  if ((!cd || cd <= 0) && frequency === 'once_per_day') cd = 86400;
  if (!cd || cd <= 0) return null;
  const remainMs = lastFireMs + cd * 1000 - nowMs;
  if (remainMs > 0) return 'cooldown_active';
  return null;
}

/**
 * `null` if cap is not reached (or cap is `0` = unlimited),
 * or `'impression_cap'` otherwise.
 */
export function evaluateImpressionCap(
  maxImpressionsPerSession: number | null | undefined,
  currentCount: number | null | undefined,
): AutoLaunchSkipReason | null {
  const max = Math.max(0, Math.floor(Number(maxImpressionsPerSession ?? 1)));
  const cur = Math.max(0, Math.floor(Number(currentCount ?? 0)));
  if (max === 0) return null;
  if (cur >= max) return 'impression_cap';
  return null;
}

// ---------------------------------------------------------------------------
// Composite decision (called from the React hook before scheduling)
// ---------------------------------------------------------------------------

export interface AutoLaunchDecisionInputs {
  readonly enabled: boolean;
  readonly isStateless: boolean;
  readonly hasActiveConversation: boolean;
  readonly hasResumedHistory: boolean;
  readonly sessionDone: boolean;
  readonly frequency: AutoLaunchFrequency;
  readonly cooldownSec: number | null | undefined;
  readonly lastFireMs: number;
  readonly manualCloseLastMs: number;
  readonly manualCloseCooldownMs: number;
  readonly maxImpressionsPerSession: number | null | undefined;
  readonly impressionCount: number;
  readonly nowMs: number;
  readonly path: string;
  readonly pathInclude: readonly string[] | null | undefined;
  readonly pathExclude: readonly string[] | null | undefined;
  readonly device: AutoLaunchDevice | string | null | undefined;
  readonly deviceInclude: readonly string[] | null | undefined;
  readonly language: string | null | undefined;
  readonly localeInclude: readonly string[] | null | undefined;
  readonly startAt: string | null | undefined;
  readonly endAt: string | null | undefined;
}

/**
 * Evaluate every gate in the canonical TSAI order. Returns the first
 * blocking reason or `{fire: true}` if all gates pass. Identity-preserving
 * with the existing `useAutoLaunch.ts` scheduler so we can swap it in
 * without behavior drift for tenants that don't yet have targeting set.
 */
export function decideAutoLaunch(input: AutoLaunchDecisionInputs): AutoLaunchDecision {
  if (!input.enabled) return skip('disabled');
  if (input.isStateless) return skip('stateless');
  if (input.hasActiveConversation) return skip('active_conversation');
  if (input.hasResumedHistory) return skip('resumed_history');

  if (input.frequency !== 'always' && input.sessionDone) return skip('session_done');

  if (input.manualCloseCooldownMs > 0 && input.manualCloseLastMs > 0) {
    if (input.nowMs - input.manualCloseLastMs < input.manualCloseCooldownMs) {
      return skip('manual_close_cooldown');
    }
  }

  const cap = evaluateImpressionCap(input.maxImpressionsPerSession, input.impressionCount);
  if (cap) return skip(cap);

  const cooldown = evaluateCooldown({
    frequency: input.frequency,
    cooldownSec: input.cooldownSec,
    lastFireMs: input.lastFireMs,
    nowMs: input.nowMs,
  });
  if (cooldown) return skip(cooldown);

  const schedule = evaluateScheduleWindow(input.nowMs, input.startAt, input.endAt);
  if (schedule) return skip(schedule);

  if (!matchesPathFilter(input.path, input.pathInclude, input.pathExclude)) {
    const exc = normalizeArray(input.pathExclude);
    const p = typeof input.path === 'string' && input.path.length > 0 ? input.path : '/';
    if (exc.some((prefix) => p === prefix || p.startsWith(prefix))) return skip('path_excluded');
    return skip('path_not_included');
  }

  if (!matchesDeviceFilter(input.device, input.deviceInclude)) return skip('device_not_included');
  if (!matchesLocaleFilter(input.language, input.localeInclude)) return skip('locale_not_included');

  return FIRE;
}

// ---------------------------------------------------------------------------
// Device detection (kept here so tests can pin the regex without importing
// the React hook). Errs toward 'desktop' on ambiguous UAs — matches TSAI.
// ---------------------------------------------------------------------------

/**
 * Coarse device classifier. Returns 'tablet' for iPad-class viewports,
 * 'mobile' for phone UAs, otherwise 'desktop'. Matches TSAI's three-bucket
 * coverage; we deliberately do not surface finer-grained values.
 */
export function detectDeviceFromUserAgent(userAgent: string | null | undefined): AutoLaunchDevice {
  const ua = String(userAgent ?? '').toLowerCase();
  if (!ua) return 'desktop';
  if (/ipad|android(?!.*mobile)|tablet/.test(ua)) return 'tablet';
  if (/iphone|ipod|android.*mobile|mobile|phone/.test(ua)) return 'mobile';
  return 'desktop';
}
